import { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';
import { useI18n } from '../lib/i18n';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const DISMISS_KEY = 'pannini.installPromptDismissed';

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

export function InstallBanner() {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // ya está instalada
    if (localStorage.getItem(DISMISS_KEY)) return;

    function onPrompt(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    }
    window.addEventListener('beforeinstallprompt', onPrompt);

    if (isIOS()) {
      // iOS no dispara beforeinstallprompt — mostramos hint manual
      setIosHint(true);
      setShow(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  async function onInstall() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setShow(false);
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  }

  if (!show) return null;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-cyan/40 bg-cyan/8 p-4">
      <button
        onClick={dismiss}
        aria-label={t('common.close')}
        className="absolute top-2 right-2 grid h-7 w-7 place-items-center rounded-full text-haze hover:text-foam hover:bg-white/10 transition"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center bg-cyan/15 text-cyan border border-cyan/30">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="font-display font-extrabold text-base text-foam">{t('install.title')}</div>
          {iosHint ? (
            <div className="mt-1 text-2xs text-haze leading-relaxed">
              <span dangerouslySetInnerHTML={{ __html: t('install.iosHint').replace('<icon/>', `<span style="display:inline-block;vertical-align:middle;"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span>`) }} />
            </div>
          ) : (
            <div className="mt-1 text-2xs text-haze leading-relaxed">
              {t('install.androidDesc')}
            </div>
          )}
        </div>
      </div>
      {!iosHint && deferred && (
        <button
          onClick={onInstall}
          className="mt-3 btn-primary w-full text-sm py-2"
        >
          <Download size={14} /> {t('install.cta')}
        </button>
      )}
      <div className="pointer-events-none absolute -bottom-10 -right-10 h-32 w-32 rounded-full bg-cyan/15 blur-2xl" />
    </div>
  );
}
